import { ParsedDoc } from '../types';
import { slugify } from './slugify';

export interface DocSearchResult {
  slug: string;
  pageTitle: string;
  /** Set when the match is inside a section, so the result can link to it. */
  headingId?: string;
  headingText?: string;
  snippet: string;
}

interface DocSection {
  headingId?: string;
  headingText?: string;
  text: string;
}

const MAX_RESULTS = 20;
const SNIPPET_RADIUS = 60;

/** Reduces markdown to readable text so snippets don't show link syntax, table pipes or callout markers. */
function toPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\[!INFO\]/gi, ' ')
    .replace(/^\s*\|?\s*-{3,}.*$/gm, ' ')
    .replace(/[*_`>#|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitIntoSections(doc: ParsedDoc): DocSection[] {
  const sections: DocSection[] = [{ text: '' }];

  for (const line of doc.body.split('\n')) {
    const heading = /^(##|###)\s+(.+)$/.exec(line);
    if (heading) {
      const headingText = heading[2].trim();
      sections.push({ headingId: slugify(headingText), headingText, text: '' });
    } else {
      sections[sections.length - 1].text += `${line}\n`;
    }
  }

  return sections.map((section) => ({ ...section, text: toPlainText(section.text) }));
}

function buildSnippet(text: string, firstTerm: string): string {
  const index = text.toLowerCase().indexOf(firstTerm);
  if (index === -1) return text.slice(0, SNIPPET_RADIUS * 2);

  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(text.length, index + firstTerm.length + SNIPPET_RADIUS);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

/**
 * Finds docs sections that contain every word of the query. Page titles rank
 * above section headings, which rank above body text.
 */
export function searchDocs(docs: ParsedDoc[], query: string): DocSearchResult[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];

  const scored: Array<{ result: DocSearchResult; score: number }> = [];

  for (const doc of docs) {
    const { title, description = '', slug } = doc.frontmatter;

    for (const section of splitIntoSections(doc)) {
      const isIntro = !section.headingId;
      const label = isIntro ? `${title} ${description}` : (section.headingText ?? '');
      const haystack = `${label} ${section.text}`.toLowerCase();
      if (!terms.every((term) => haystack.includes(term))) continue;

      const lowerLabel = label.toLowerCase();
      const labelMatches = terms.filter((term) => lowerLabel.includes(term)).length;
      const score = labelMatches * (isIntro ? 3 : 2) + 1;
      const snippetSource = section.text || description;

      scored.push({
        score,
        result: {
          slug,
          pageTitle: title,
          headingId: section.headingId,
          headingText: section.headingText,
          snippet: buildSnippet(snippetSource, terms[0]),
        },
      });
    }
  }

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map(({ result }) => result);
}
